import {
  FormFieldOption,
  GridFieldOption,
  GridFilterOption,
  IBaseModel,
  IBaseRepository,
  IListFetchParams,
  IListResponse,
  IMapListToKeyValue,
} from './commonInterfaces';
import { AxiosResponse } from 'axios';
import { HttpClient } from './apiClient';
import { CrudID, MODULE } from './commonTypes';
import { queryString } from './commonFunctions';
import * as z from 'zod';

export class ApiResponse<T> {
  data?: T;
  succeeded?: boolean;
  errors?: never;
}

const transform = (response: AxiosResponse): Promise<ApiResponse<never>> => {
  return new Promise((resolve) => {
    const result: ApiResponse<never> = {
      data: response.data,
      succeeded: response.status === 200,
      errors: response.data?.errors,
    };
    resolve(result);
  });
};

export abstract class BaseRepository<
    T extends IBaseModel,
    Q extends IListFetchParams,
  >
  extends HttpClient
  implements IBaseRepository<T, Q>
{
  private _moduleName: MODULE;
  private _element: T | undefined;
  private _list: IListResponse<T>;
  private _keyValueMapFields: IMapListToKeyValue<T>;
  private _gridColumnOptions: GridFieldOption<T>[];
  private _gridFiltersOptions: GridFilterOption<Q>[];
  private _formFieldsOptions: FormFieldOption<T>[];
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  private _formValidationSchema: z.ZodObject<any>;
  private _hook: React.FC | undefined;

  constructor(moduleName: MODULE) {
    super();
    this._moduleName = moduleName;
    this._list = { data: [], totalCount: 0 };
    this._keyValueMapFields = {};
    this._gridColumnOptions = [];
    this._gridFiltersOptions = [];
    this._formFieldsOptions = [];
    this._formValidationSchema = z.object({});
    this._hook = undefined;
  }

  get moduleName(): MODULE {
    return this._moduleName;
  }

  get formFieldsOptions(): FormFieldOption<T>[] {
    return this._formFieldsOptions;
  }

  set formFieldsOptions(columns: FormFieldOption<T>[]) {
    this._formFieldsOptions = columns;
  }

  get formValidationSchema(): z.ZodObject<any> {
    return this._formValidationSchema;
  }

  set formValidationSchema(validationSchema: z.ZodObject<any>) {
    this._formValidationSchema = validationSchema;
  }

  get gridFieldsOptions(): GridFieldOption<T>[] {
    return this._gridColumnOptions;
  }

  set gridFieldsOptions(columns: GridFieldOption<T>[]) {
    this._gridColumnOptions = columns;
  }

  get gridFiltersOptions(): GridFilterOption<Q>[] {
    return this._gridFiltersOptions;
  }

  set gridFiltersOptions(columns: GridFilterOption<Q>[]) {
    this._gridFiltersOptions = columns;
  }

  get keyValueMapFields(): IMapListToKeyValue<T> {
    return this._keyValueMapFields;
  }

  set keyValueMapFields(data: IMapListToKeyValue<T>) {
    this._keyValueMapFields = data;
  }

  get list(): IListResponse<T> {
    return this._list;
  }

  set list(data: IListResponse<T>) {
    this._list = data;
  }

  get element(): T | undefined {
    return this._element;
  }

  set element(data: T | undefined) {
    this._element = data;
  }

  get hook() {
    return this._hook;
  }

  set hook(component) {
    this._hook = component;
  }

  public async get(id: CrudID): Promise<ApiResponse<T>> {
    const instance = this.createInstance();
    const result = await instance
      .get(`/${this._moduleName.toLowerCase()}/${id}`)
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async getMany(params?: Q): Promise<ApiResponse<IListResponse<T>>> {
    const query = params ? queryString<Q>(params) : '';
    const instance = this.createInstance();
    const result = await instance
      .get(`/${this._moduleName.toLowerCase()}${query}`)
      .then(transform);
    return result as ApiResponse<IListResponse<T>>;
  }

  public async create(item: T): Promise<ApiResponse<T>> {
    const instance = this.createInstance();
    const result = await instance
      .post(`/${this._moduleName.toLowerCase()}`, item)
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async update(id: CrudID, item: T): Promise<ApiResponse<T>> {
    const instance = this.createInstance();
    const itemToUpdate = { ...item };
    delete itemToUpdate['id'];
    delete itemToUpdate['createdAt'];
    delete itemToUpdate['updatedAt'];
    const result = await instance
      .patch(`/${this._moduleName.toLowerCase()}/${id}`, itemToUpdate)
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async delete(id: CrudID): Promise<ApiResponse<T>> {
    const instance = this.createInstance();
    const result = await instance
      .delete(`/${this._moduleName.toLowerCase()}/${id}`)
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async createOrUpdate(id: CrudID, item: T): Promise<ApiResponse<T>> {
    return id ? this.update(id, item) : this.create(item);
  }
}

export class CustomApiClient extends HttpClient {
  public async get<T>(url: string, id: CrudID): Promise<ApiResponse<T>> {
    const instance = this.createInstance();
    const result = await instance.get(`/${url}/${id}`).then(transform);
    return result as ApiResponse<T>;
  }

  public async getWithParams<T, Q extends IListFetchParams>(
    url: string,
    params?: Q,
  ): Promise<ApiResponse<T>> {
    const query = params ? queryString<Q>(params) : '';
    const instance = this.createInstance();
    const result = await instance.get(`/${url}${query}`).then(transform);
    return result as ApiResponse<T>;
  }

  public async getMany<T extends IBaseModel, Q extends IListFetchParams>(
    url: string,
    params?: Q,
  ): Promise<ApiResponse<IListResponse<T>>> {
    const query = params ? queryString<Q>(params) : '';
    const instance = this.createInstance();
    const result = await instance.get(`/${url}${query}`).then(transform);
    return result as ApiResponse<IListResponse<T>>;
  }

  /** `timeout` in ms per le operazioni piu' lunghe del default. */
  public async create<T>(
    url: string,
    item: T,
    timeout?: number,
  ): Promise<ApiResponse<T>> {
    const instance = this.createInstance(timeout);
    const result = await instance.post(`/${url}`, item).then(transform);
    return result as ApiResponse<T>;
  }

  public async postFormData<T>(
    url: string,
    formData: FormData,
    timeout?: number,
  ): Promise<ApiResponse<T>> {
    const instance = this.createInstance(timeout);
    const result = await instance
      .post(`/${url}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async update<T>(
    url: string,
    id: CrudID,
    item: T,
  ): Promise<ApiResponse<T>> {
    const instance = this.createInstance();
    const itemToUpdate = { ...item };
    const result = await instance
      .patch(`/${url}/${id}`, itemToUpdate)
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async delete<T>(url: string, id: CrudID): Promise<ApiResponse<T>> {
    const instance = this.createInstance();
    const result = await instance.delete(`/${url}/${id}`).then(transform);
    return result as ApiResponse<T>;
  }

  public async createOrUpdate<T>(
    url: string,
    id: CrudID,
    item: T,
  ): Promise<ApiResponse<T>> {
    return id ? this.update<T>(url, id, item) : this.create<T>(url, item);
  }
}
